import React, { useRef, useState } from "react";
import { authStore } from "../lib/api";
import { toast } from "./ui/sonner";
import { uploadChunked } from "../lib/upload";
import { uploadScannedDocument } from "../lib/documentScanner";
import FinanceDocumentScanner from "./finance/FinanceDocumentScanner";
import DocumentScanVersions from "./finance/DocumentScanVersions";
import { Camera, Paperclip, Loader2, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const fileHref = (fid, thumb = false) => `${BACKEND}/api/files/${fid}/${thumb ? "thumb" : "download"}?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;

/* 信用卡消费收据：缩略图 + 手机拍照 / 选文件上传（分片 → 对象存储 → 挂到明细） */
export const ReceiptCell = ({ tx, zh, api, onChanged, canDelete = false, size = "sm" }) => {
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [scanFiles, setScanFiles] = useState([]);
  const r = tx.receipt;
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const up = await (file.documentScan ? uploadScannedDocument : uploadChunked)(api, file);
      await api.post(`/finance/credit-cards/transactions/${tx.id}/receipt`, { fileId: up.fileId, filename: up.filename || file.name, contentType: up.contentType || file.type });
      toast.success(zh ? "收据已挂到该消费" : "Receipt attached"); onChanged?.();
    } catch (e) { toast.error(e?.detailText || (zh ? "上传失败" : "Upload failed")); } finally { setBusy(false); }
  };
  const choose = event => {
    const files = Array.from(event.target.files || []); event.target.value = "";
    if (!busy && files.length) setScanFiles(files);
  };
  const prepared = ({ scanFile, originals }) => {
    // 一笔消费仍只有一个主收据，多张照片必须合为一个扫描 PDF，不能静默丢掉后续照片。
    if (!scanFile && originals.length !== 1) { toast.error(zh ? "请将多张照片合成为一份 PDF。" : "Combine the photos into one PDF."); return; }
    setScanFiles([]); upload(scanFile || originals[0]);
  };
  const remove = async () => {
    if (!window.confirm(zh ? "移除该收据？" : "Remove receipt?")) return;
    try { await api.delete(`/finance/credit-cards/transactions/${tx.id}/receipt`); onChanged?.(); } catch { toast.error(zh ? "移除失败" : "Failed"); }
  };
  const big = size === "lg";
  return (
    <div className={`flex items-center gap-1.5 ${big ? "" : ""}`} data-testid={`cc-receipt-${tx.id}`} data-has={r ? "1" : "0"}>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`cc-receipt-camera-input-${tx.id}`} onChange={choose} />
      <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" data-testid={`cc-receipt-file-input-${tx.id}`} onChange={choose} />
      {r ? (
        <a href={fileHref(r.fileId)} target="_blank" rel="noreferrer" data-testid={`cc-receipt-view-${tx.id}`} title={`${r.filename} · ${r.by}`} className={`relative block ${big ? "h-16 w-16" : "h-8 w-8"} rounded-md overflow-hidden border border-slate-200 bg-slate-50 shrink-0`}>
          {String(r.contentType || "").startsWith("image/") ? <img src={fileHref(r.fileId, true)} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <Paperclip className="h-4 w-4 m-auto mt-2 text-slate-500" />}
        </a>
      ) : (
        <span className={`text-[10px] ${big ? "text-red-600 font-semibold" : "text-red-500"}`} data-testid={`cc-receipt-missing-${tx.id}`}>{zh ? "无收据" : "none"}</span>
      )}
      <button type="button" data-testid={`cc-receipt-camera-${tx.id}`} disabled={busy} onClick={() => camRef.current?.click()} title={zh ? "手机拍照上传收据" : "Take photo"}
        className={`inline-flex items-center gap-1 rounded-md ${big ? "bg-brand-500 hover:bg-brand-600 text-white px-2.5 py-1.5 text-xs font-semibold" : "text-slate-400 hover:text-brand-500 p-0.5"}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className={big ? "h-4 w-4" : "h-3.5 w-3.5"} />}{big && (zh ? "拍收据" : "Photo")}
      </button>
      <button type="button" data-testid={`cc-receipt-file-${tx.id}`} disabled={busy} onClick={() => fileRef.current?.click()} title={zh ? "从相册 / 文件上传" : "Upload file"}
        className={`inline-flex items-center gap-1 rounded-md ${big ? "border border-slate-300 text-slate-700 px-2.5 py-1.5 text-xs" : "text-slate-400 hover:text-slate-800 p-0.5"}`}>
        <Paperclip className={big ? "h-4 w-4" : "h-3.5 w-3.5"} />{big && (zh ? "相册 / 文件" : "File")}
      </button>
      {r && canDelete && <button type="button" data-testid={`cc-receipt-del-${tx.id}`} onClick={remove} className="text-slate-300 hover:text-red-600 p-0.5" title={zh ? "移除收据" : "Remove"}><X className="h-3.5 w-3.5" /></button>}
      {(r?.documentScan || r?.hasDocumentScan) && <DocumentScanVersions api={api} zh={zh} fileId={r.fileId} />}
      <FinanceDocumentScanner api={api} zh={zh} files={scanFiles} maxOriginalFiles={1} maxBytes={25 * 1024 * 1024} onClose={() => setScanFiles([])} onPrepared={prepared} />
    </div>
  );
};
